<script setup lang="ts">
import type { VTableColumnChoice } from "./VTable.types";
import { onClickOutside } from "@vueuse/core";
import { computed, nextTick, ref, useId } from "vue";
import { useI18n } from "vue-i18n";
import VCheckbox from "../Checkbox/VCheckbox.vue";
import VDropdown from "../Dropdown/VDropdown.vue";
import VTableToolbarButton from "./VTableToolbarButton.vue";

defineOptions({
  name: "VTableColumnsMenu",
});

/** The columns button of the toolbar: shows or hides columns, one checkbox per column. */
const { columns } = defineProps<{
  columns: VTableColumnChoice[]
}>();

const emit = defineEmits<{
  toggle: [key: string, visible: boolean]
  showAll: []
}>();

const { t } = useI18n();
const uid = useId();
const wrapperRef = ref<HTMLElement | null>(null);
const open = ref(false);
const allShown = computed(() => columns.every(column => column.visible));

/** Closes the popover and puts focus back on its button, so Escape works from the button and from the panel. */
function closeAndRefocus() {
  if (!open.value) {
    return;
  }
  open.value = false;
  void nextTick(() => wrapperRef.value?.querySelector<HTMLElement>("button")?.focus());
}

onClickOutside(wrapperRef, () => {
  open.value = false;
}, { ignore: [".v-popper__popper"] });
</script>

<template>
  <div ref="wrapperRef" class="inline-flex shrink-0" @keydown.esc="closeAndRefocus">
    <VDropdown
      theme="verbleif-popper"
      placement="bottom-end"
      :distance="6"
      :shown="open"
      :triggers="[]"
      :auto-hide="false"
      @hide="open = false"
    >
      <VTableToolbarButton
        :label="t('base.table.columns_tooltip')"
        :open="open"
        icon-only
        haspopup="dialog"
        data-testid="table-columns-button"
        @click="open = !open"
      >
        <template #icon>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M9 4v16M15 4v16" />
          </svg>
        </template>
      </VTableToolbarButton>
      <template #popper>
        <div
          v-if="open"
          class="flex w-[260px] flex-col gap-0.5 p-1.5 text-sm"
          role="dialog"
          :aria-labelledby="`${uid}-title`"
          data-testid="table-columns-menu"
          @keydown.esc.stop="closeAndRefocus"
        >
          <div :id="`${uid}-title`" class="px-2.5 pt-2 pb-2.5 text-sm font-semibold text-gray-800 dark:text-gray-200">
            {{ t("base.table.columns_title") }}
          </div>
          <div role="group" :aria-labelledby="`${uid}-title`" class="flex flex-col">
            <VCheckbox
              v-for="column in columns"
              :key="column.key"
              :model-value="column.visible"
              :name="`${uid}-${column.key}`"
              :label-right="column.label"
              :disabled="column.locked"
              class="min-h-9 rounded-md px-2.5 hover:bg-gray-100 dark:hover:bg-dark-700"
              @update:model-value="(checked: unknown) => emit('toggle', column.key, Boolean(checked))"
            />
          </div>
          <div class="mt-1 border-t border-gray-200 px-1 pt-1.5 dark:border-dark-600">
            <button
              type="button"
              class="h-7 cursor-pointer rounded-full px-2 text-xs font-medium text-primary-700 hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 disabled:pointer-events-none disabled:opacity-40 dark:text-primary-400 dark:hover:bg-dark-700"
              :disabled="allShown"
              @click="emit('showAll')"
            >
              {{ t("base.table.columns_show_all") }}
            </button>
          </div>
        </div>
      </template>
    </VDropdown>
  </div>
</template>
